/**
 * Legacy Adapter for RecipientPicker
 * Provides backward compatibility with old comma-separated string format
 *
 * This allows gradual migration by supporting both old and new APIs
 */

import React, { useMemo, useCallback } from 'react';
import { RecipientPicker } from './RecipientPicker';
import { RecipientPickerProps, LegacyRecipientProps, RecipientUtils } from './types';

type LegacyRecipientPickerProps = Omit<RecipientPickerProps, 'value' | 'onChange'> & LegacyRecipientProps;

/**
 * Legacy RecipientPicker that accepts comma-separated strings
 * Use this for gradual migration from old components
 */
export const LegacyRecipientPicker: React.FC<LegacyRecipientPickerProps> = ({
  userIds,
  groupIds,
  onChange: legacyOnChange,
  ...modernProps
}) => {
  // Convert legacy format to new format
  const value = useMemo(() => {
    return RecipientUtils.fromLegacy(userIds, groupIds);
  }, [userIds, groupIds]);

  // Convert new format back to legacy format for callback
  const handleChange = useCallback((selection: ReturnType<typeof RecipientUtils.fromLegacy>) => {
    if (legacyOnChange) {
      const { userIds: newUserIds, groupIds: newGroupIds } = RecipientUtils.toLegacy(selection);
      legacyOnChange(newUserIds, newGroupIds);
    }
  }, [legacyOnChange]);

  return (
    <RecipientPicker
      {...modernProps}
      value={value}
      onChange={handleChange}
    />
  );
};

/**
 * Helper function to migrate from legacy to modern API
 * Use this in your migration code
 */
export const migrateLegacyProps = (
  legacyProps: LegacyRecipientProps
): Pick<RecipientPickerProps, 'value' | 'onChange'> => {
  return {
    value: RecipientUtils.fromLegacy(legacyProps.userIds, legacyProps.groupIds),
    onChange: (selection) => {
      if (legacyProps.onChange) {
        const { userIds, groupIds } = RecipientUtils.toLegacy(selection);
        legacyProps.onChange(userIds, groupIds);
      }
    }
  };
};

export default LegacyRecipientPicker;
